import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../services/supabaseClient'

export default function Dashboard() {
  const { user, signOut } = useAuth()
  const email = user?.email || ''
  const [checkingLink, setCheckingLink] = useState(true)
  const [student, setStudent] = useState(null)
  const [todayLoading, setTodayLoading] = useState(false)
  const [todayError, setTodayError] = useState('')
  const [scheduleSummary, setScheduleSummary] = useState(null)
  const [homeworkDueCount, setHomeworkDueCount] = useState(null)
  const [attendanceSummary, setAttendanceSummary] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    let active = true
    ;(async () => {
      const { data, error } = await supabase.rpc('get_current_student')
      if (!active) return
      if (error) {
        setCheckingLink(false)
        return
      }
      if (!data || data.length === 0) {
        navigate('/link-account', { replace: true })
        return
      }
      setStudent(data[0])
      setCheckingLink(false)
    })()
    return () => { active = false }
  }, [navigate])

  useEffect(() => {
    if (checkingLink || !student) return
    let active = true
    ;(async () => {
      setTodayLoading(true)
      setTodayError('')
      setScheduleSummary(null)
      setHomeworkDueCount(null)
      setAttendanceSummary(null)
      try {
        const today = new Date()
        const todayStr = today.toISOString().slice(0, 10)

        const [scheduleRes, assignmentsRes, attendanceRes] = await Promise.all([
          supabase.rpc('get_student_today_schedule'),
          supabase.rpc('get_student_assignments', { p_from: todayStr, p_to: todayStr }),
          supabase.rpc('get_student_attendance', { p_from: todayStr, p_to: todayStr }),
        ])

        if (!active) return

        if (scheduleRes.error) throw scheduleRes.error
        if (assignmentsRes.error) throw assignmentsRes.error
        if (attendanceRes.error) throw attendanceRes.error

        const schedule = scheduleRes.data || []
        if (schedule.length > 0) {
          setScheduleSummary({
            count: schedule.length,
            firstSubject: schedule[0].subject_name || '',
            firstStart: schedule[0].start_time ? String(schedule[0].start_time).slice(0, 5) : '',
          })
        } else {
          setScheduleSummary({ count: 0, firstSubject: '', firstStart: '' })
        }

        const assignments = assignmentsRes.data || []
        const homeworkToday = assignments.filter(a => a.type === 'homework')
        setHomeworkDueCount(homeworkToday.length)

        const attendance = attendanceRes.data || []
        if (attendance.length > 0) {
          const statuses = attendance.map(r => r.status).filter(Boolean)
          let label = ''
          if (statuses.includes('absent')) label = 'غياب'
          else if (statuses.includes('late')) label = 'متأخر'
          else if (statuses.includes('excused')) label = 'مُعذَر'
          else if (statuses.includes('present')) label = 'حاضر'
          setAttendanceSummary(label ? { label } : null)
        } else {
          setAttendanceSummary(null)
        }
      } catch (err) {
        if (!active) return
        setTodayError(err.message || 'تعذر تحميل بيانات اليوم')
      } finally {
        if (!active) return
        setTodayLoading(false)
      }
    })()
    return () => { active = false }
  }, [checkingLink, student])

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        padding: '1.5rem',
      }}
    >
      {checkingLink ? (
        <div style={{ textAlign: 'center', marginTop: '3rem', color: '#4b5563' }}>
          جاري التحقق من ربط الحساب...
        </div>
      ) : (
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: 16,
              padding: 16,
              boxShadow: '0 10px 25px rgba(15,23,42,0.08)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <h1 style={{ margin: 0, fontSize: 22 }}>لوحة الطالب</h1>
              <button
                type="button"
                onClick={signOut}
                style={{
                  padding: '6px 10px',
                  borderRadius: 999,
                  border: '1px solid #e5e7eb',
                  background: '#f9fafb',
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                تسجيل الخروج
              </button>
            </div>

            <div
              style={{
                marginBottom: 16,
                padding: 12,
                borderRadius: 12,
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
              }}
            >
              <p style={{ marginBottom: 6 }}>مرحباً بك في بوابة الطالب.</p>
              {student && (
                <p style={{ marginBottom: 4 }}>
                  الطالب: <strong>{student.name}</strong> (رمز: {student.code})
                </p>
              )}
              {email && (
                <p style={{ marginBottom: 0 }}>
                  تم تسجيل الدخول بالبريد: <strong>{email}</strong>
                </p>
              )}
            </div>

            <div
              style={{
                marginBottom: 16,
                padding: 12,
                borderRadius: 12,
                background: '#eff6ff',
                border: '1px solid #dbeafe',
              }}
            >
              <h2 style={{ margin: '0 0 8px', fontSize: 16 }}>اليوم في لمحة</h2>
              {todayLoading ? (
                <div style={{ fontSize: 14 }}>جارٍ تحميل بيانات اليوم...</div>
              ) : todayError ? (
                <div style={{ fontSize: 13, color: '#b91c1c' }}>{todayError}</div>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 14 }}>
                  <li style={{ marginBottom: 4 }}>
                    <strong>الجدول:</strong>{' '}
                    {scheduleSummary && scheduleSummary.count > 0
                      ? `${scheduleSummary.count} حصة${
                          scheduleSummary.firstSubject
                            ? ` (أولى حصة: ${scheduleSummary.firstSubject}${
                                scheduleSummary.firstStart ? ` ${scheduleSummary.firstStart}` : ''
                              })`
                            : ''
                        }`
                      : 'لا يوجد حصص اليوم.'}
                  </li>
                  <li style={{ marginBottom: 4 }}>
                    <strong>الواجبات:</strong>{' '}
                    {typeof homeworkDueCount === 'number'
                      ? homeworkDueCount === 0
                        ? 'لا يوجد واجبات مستحقة اليوم.'
                        : `${homeworkDueCount} واجب/واجبات مستحقة اليوم.`
                      : '—'}
                  </li>
                  <li>
                    <strong>الحضور:</strong>{' '}
                    {attendanceSummary
                      ? attendanceSummary.label
                      : 'لا يوجد تسجيل حضور لليوم بعد.'}
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
